import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../../components/ui/select';
import { Input } from '../../../../../../../components/ui/input';
import { Button } from '../../../../../../../components/ui/button';
import { Badge } from '../../../../../../../components/ui/badge';
import { AlertCircle, Edit3, Loader2 } from 'lucide-react';
import { renamePass } from '../../../../../../../redux/device/actions';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../../../../components/ui/table';
import Notification from '../../../../../../../components/ui/custom-notification';

interface RenamePassProps {
    jobData: any;
    projectId?: number;
    pipeParamKey?: string;
    onRenameSuccess?: () => void;
}

interface PassData {
    PassName: string;
    PassPendName: string;
    PassEnable: string;
    PassGroupNumber: string;
    EndingAngle: string;
    Type: string;
    Torch?: string;
    [key: string]: any;
}

interface RenameEntry {
    groupNo: string;
    newPassName: string;
    numberToAppend: string;
}

const RenamePass: React.FC<RenamePassProps> = ({ jobData, projectId, pipeParamKey, onRenameSuccess }) => {
    const dispatch = useDispatch();
    const [renameEntries, setRenameEntries] = useState<Map<string, RenameEntry>>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [validationError, setValidationError] = useState<string>('');
    const [missingFields, setMissingFields] = useState<Set<string>>(new Set());

    // Reset rename entries and loading when job data or pipe param key changes
    React.useEffect(() => {
        setRenameEntries(new Map());
        setIsLoading(false);
        // Clear any pending timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [jobData?.job_number, pipeParamKey]);

    // Pass name options for dropdowns
    const passNameOptions = [
        { value: 'Hot', label: 'Hot' },
        { value: 'Root', label: 'Root' },
        { value: 'Fill', label: 'Fill' },
        { value: 'Last Fill', label: 'Last Fill' },
        { value: 'Strip', label: 'Strip' },
        { value: 'Cap', label: 'Cap' },
        { value: 'Disabled', label: 'Disabled' },
        { value: 'Weave', label: 'Weave' },
    ];

    // Generate numbers 1-20 for append dropdown
    const numberOptions = Array.from({ length: 20 }, (_, i) => ({
        value: String(i + 1),
        label: String(i + 1)
    }));

    // Extract all PASS type entries from parsed_json_data or PIPE PARAMETERS
    const passEntries = useMemo(() => {
        if (!jobData?.parsed_json_data) return [];
       // console.log('jobData.parsed_json_data', jobData.parsed_json_data);

        const parsedData = jobData.parsed_json_data;
        const params: PassData[] = [];

        // Check if parsed_json_data is an array
        if (Array.isArray(parsedData)) {
            // Try to get PASS type entries first
            const passTypeEntries = parsedData.filter((item: any) => item.Type === 'PASS');
            if (passTypeEntries.length > 0) {
                return passTypeEntries;
            }
        }

        // Extract from PIPE PARAMETERS structure (when parsed_json_data is an object)
        if (typeof parsedData === 'object' && parsedData !== null && !Array.isArray(parsedData)) {
            // Filter by provided pipeParamKey, or find all PIPE PARAMETERS keys
            let pipeParamKeys: string[] = [];
            if (pipeParamKey) {
                // Use the provided pipe parameter key
                if (parsedData[pipeParamKey]) {
                    pipeParamKeys = [pipeParamKey];
                }
            } else {
                // Fallback: find all PIPE PARAMETERS keys
                pipeParamKeys = Object.keys(parsedData).filter(key => key.includes('PIPE PARAMETERS'));
            }

            for (const currentPipeParamKey of pipeParamKeys) {
                const pipeParams = parsedData[currentPipeParamKey];

                if (pipeParams && typeof pipeParams === 'object') {
                    const headers = pipeParams.headers || {};

                    // Extract torch from pipeParamKey displayName first (e.g., "PIPE PARAMETERS;18_Katy" -> "18_Katy")
                    let globalTorchId = '';
                    const keyParts = currentPipeParamKey.split(';');
                    if (keyParts.length > 1) {
                        globalTorchId = keyParts[1].trim();
                    }

                    // Extract pass groups from headers (DA, DB, DC, etc.)
                    for (const [groupKey, groupData] of Object.entries(headers)) {
                        const passHeader = groupData as any;

                        // Get pass name from header
                        const passName = passHeader.name || '';

                        // Get zones to extract pass pendant names
                        const zones = passHeader.zones || {};
                        const zoneKeys = Object.keys(zones);

                        // Extract torch from values if available, otherwise use global
                        let torchId = globalTorchId;
                        const values = pipeParams.values || {};

                        // Look for torch information in values
                        for (const category of Object.keys(values)) {
                            const categoryData = values[category];
                            if (!categoryData || typeof categoryData !== 'object') continue;

                            for (const paramKey of Object.keys(categoryData)) {
                                const paramKeyLower = paramKey.toLowerCase();
                                if (paramKeyLower.includes('torch') || paramKeyLower.includes('torchid') || paramKeyLower.includes('torch id')) {
                                    const torchParam = categoryData[paramKey];

                                    // Check if torchParam has displayName directly
                                    if (torchParam?.displayName) {
                                        torchId = String(torchParam.displayName);
                                        break;
                                    }

                                    // Check in value array
                                    if (torchParam?.value && Array.isArray(torchParam.value)) {
                                        // Try to find group-specific first, then global
                                        const torchValue = torchParam.value.find((v: any) =>
                                            v.groupName === groupKey
                                        ) || torchParam.value.find((v: any) =>
                                            v.groupName === 'D' || !v.groupName
                                        );

                                        if (torchValue) {
                                            // Use displayName if available, otherwise use value
                                            torchId = String(torchValue.displayName || torchValue.value || '');
                                            console.log('Torch from value array:', torchId, 'for group:', groupKey);
                                            break;
                                        }
                                    }

                                    // Check if torchParam.text exists
                                    if (torchParam?.text && !torchId) {
                                        torchId = String(torchParam.text);
                                    }
                                }
                            }
                            if (torchId && torchId !== globalTorchId) break;
                        }

                        // Create entries for each zone or one entry if no zones
                        if (zoneKeys.length > 0) {
                            // Create one entry per zone
                            for (const zoneKey of zoneKeys) {
                                const zone = zones[zoneKey] as any;
                                params.push({
                                    Type: 'PASS',
                                    PassName: passName,
                                    PassPendName: zone.name || zoneKey,
                                    PassEnable: 'Yes',
                                    PassGroupNumber: groupKey,
                                    EndingAngle: '',
                                    Torch: torchId,
                                });
                            }
                        } else {
                            // No zones, create single entry for the group
                            params.push({
                                Type: 'PASS',
                                PassName: passName,
                                PassPendName: '',
                                PassEnable: 'Yes',
                                PassGroupNumber: groupKey,
                                EndingAngle: '',
                                Torch: torchId,
                            });
                        }
                    }
                }
            }
        }

        // console.log('Extracted passEntries:', params);
        //  console.log('Total passEntries:', params.length);
        return params;
    }, [jobData, pipeParamKey]);

    // Group passes by Group Number only (not by LEAD/TRAIL)
    const groupedPasses = useMemo(() => {
        const grouped = new Map<string, { passes: PassData[]; torch: string }>();

        passEntries.forEach((pass: PassData) => {
            const groupKey = pass.PassGroupNumber || 'Unknown';
            const torchId = pass.Torch || (pass as any).Torch || '';

            if (!grouped.has(groupKey)) {
                grouped.set(groupKey, { passes: [], torch: torchId });
            }

            const group = grouped.get(groupKey)!;
            group.passes.push(pass);

            // Update torch if not already set (use first non-empty torch value)
            if (!group.torch && torchId) {
                group.torch = torchId;
            }
        });

        // Sort groups: handle alphanumeric sorting (DA, DB, DC, etc.)
        return Array.from(grouped.entries()).sort((a, b) => {
            const aKey = a[0];
            const bKey = b[0];

            // Try numeric comparison first
            const aNum = parseInt(aKey);
            const bNum = parseInt(bKey);

            if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
            }

            // For alphanumeric (DA, DB, etc.), use string comparison
            return aKey.localeCompare(bKey);
        });
    }, [passEntries]);

    // Format pass name for display (extract code and parenthetical value)
    const formatPassName = (passName: string, passPendName?: string) => {
        // Handle ".." as Disabled
        if (passName.trim() === '..' || passName.trim() === '.') {
            if (passPendName && passPendName.trim()) {
                return { code: 'Disabled', value: passPendName.trim(), display: `Disabled (${passPendName.trim()})` };
            }
            return { code: 'Disabled', value: '', display: 'Disabled ()' };
        }

        // Extract pattern like "F1T (1T)" or "Disabled (OP)"
        const match = passName.match(/^([^(]+)\s*\(([^)]+)\)$/);
        if (match) {
            return { code: match[1].trim(), value: match[2].trim(), display: `${match[1].trim()} (${match[2].trim()})` };
        }

        // If pass name doesn't have parentheses but we have PassPendName, use it
        if (passPendName && passPendName.trim()) {
            return { code: passName.trim(), value: passPendName.trim(), display: `${passName.trim()} (${passPendName.trim()})` };
        }

        // If no parentheses and no pendant name
        return { code: passName.trim(), value: '', display: passName.trim() };
    };

    const handleNewPassNameChange = (groupKey: string, value: string) => {
        // Clear validation error and missing fields when user makes changes
        if (validationError) {
            setValidationError('');
        }
        if (missingFields.has(`${groupKey}-name`)) {
            const newMissingFields = new Set(missingFields);
            newMissingFields.delete(`${groupKey}-name`);
            setMissingFields(newMissingFields);
        }

        const current = renameEntries.get(groupKey) || {
            groupNo: groupKey,
            newPassName: '',
            numberToAppend: ''
        };

        const updated = {
            ...current,
            newPassName: value
        };

        const newEntries = new Map(renameEntries);
        newEntries.set(groupKey, updated);
        setRenameEntries(newEntries);
    };

    const handleNumberChange = (groupKey: string, value: string) => {
        // Clear validation error and missing fields when user makes changes
        if (validationError) {
            setValidationError('');
        }

        const current = renameEntries.get(groupKey) || {
            groupNo: groupKey,
            newPassName: '',
            numberToAppend: ''
        };

        const updated = {
            ...current,
            numberToAppend: value
        };

        const newEntries = new Map(renameEntries);
        newEntries.set(groupKey, updated);
        setRenameEntries(newEntries);
    };

    // Store timeout reference for cleanup
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Cleanup timeout on unmount or when dependencies change
    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleApplyChanges = () => {
        if (!projectId || !jobData?.job_number) {
            console.error('Missing projectId or job_number');
            return;
        }
        
        if (isLoading) {
            return; // Prevent multiple clicks while loading
        }

        // Validate that all groups have pass names selected
        const missingGroups: string[] = [];
        groupedPasses.forEach(([groupKey]) => {
            const entry = renameEntries.get(groupKey);
            if (!entry?.newPassName) {
                missingGroups.push(groupKey);
            }
        });

        if (missingGroups.length > 0) {
            // Track which fields are missing for red border highlighting
            const missingFieldsSet = new Set<string>();
            groupedPasses.forEach(([groupKey]) => {
                const entry = renameEntries.get(groupKey);
                if (!entry?.newPassName) {
                    missingFieldsSet.add(`${groupKey}-name`);
                }
            });
            setMissingFields(missingFieldsSet);
            Notification.warning('Please select pass name for all groups');
            return;
        }

        // Clear validation error and missing fields if validation passes
        setValidationError('');
        setMissingFields(new Set());
        
        // Build renamed_passes array from renameEntries
        const renamedPasses: any[] = [];

        // Iterate through each group that has a rename entry
        renameEntries.forEach((entry, groupKey) => {
            if (!entry.newPassName) {
                return; // Skip if newPassName is missing
            }

            // Find the group data
            const groupData = groupedPasses.find(([key]) => key === groupKey);
            if (!groupData) return;

            const [, groupInfo] = groupData;
            const passes = groupInfo.passes || [];
            const torchId = groupInfo.torch || '';

            // Create newPassName by combining selected name with number (e.g., "Root" + "1" = "Root 1")
            // Only append number if provided
            const newPassNameFull = entry.numberToAppend 
                ? `${entry.newPassName} ${entry.numberToAppend}`
                : entry.newPassName;

            // Add an entry for each pass in this group
            passes.forEach((pass) => {
                const formatted = formatPassName(pass.PassName || '', pass.PassPendName);
                
                // If pass is disabled, use original name; otherwise use the new selected name
                const isDisabled = pass.PassEnable && pass.PassEnable.toLowerCase() === 'no';
                const finalNewPassName = isDisabled ? pass.PassName : newPassNameFull;
                
                renamedPasses.push({
                    groupNumber: groupKey,
                    torch: torchId,
                    passName: formatted.display, // Full pass name as displayed
                    newPassName: finalNewPassName // Use original name for disabled, new name for enabled
                });
            });
        });

        if (renamedPasses.length === 0) {
            console.log('No passes to rename');
            return;
        }

        // Build the payload
        const payload = {
            project_id: projectId,
            job_number: jobData.job_number,
            is_renamed: true,
            json_payload: {
                renamed_passes: renamedPasses
            }
        };

        console.log('Applying rename changes with payload:', payload);

        // Set loading state
        setIsLoading(true);

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Dispatch Redux action to call API
        dispatch(renamePass(payload) as any);

        // Reset loading after a reasonable timeout (saga will handle success/error notifications)
        // In a production app, you'd want to listen to Redux state changes for success/error
        timeoutRef.current = setTimeout(() => {
            setIsLoading(false);
            // Reset after applying only if loading completes
            setRenameEntries(new Map());
            // Call refresh callback to reload weld parameters
            if (onRenameSuccess) {
                onRenameSuccess();
            }
            timeoutRef.current = null;
        }, 3000); // 3 second timeout - adjust based on typical API response time
    };

    return (
        <div className="flex flex-col h-full">
            {/* Sticky action toolbar at the top — scoped to Rename Pass tab */}
            <div className="flex items-center justify-end gap-3 px-6 py-3 bg-white border-b border-[#E2E5EA] flex-shrink-0">
                <Button
                    variant="outline"
                    onClick={() => setRenameEntries(new Map())}
                    className="h-9 px-4 border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                >
                    Reset
                </Button>
                <Button
                    onClick={handleApplyChanges}
                    disabled={isLoading}
                    className="h-9 px-4 bg-hero-red hover:bg-hero-red/90 text-white text-sm font-medium"
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Applying...
                        </>
                    ) : (
                        'Apply Changes'
                    )}
                </Button>
            </div>

            {/* Table */}
            <Card className="flex-1 flex flex-col overflow-hidden min-h-0 border-0 shadow-none rounded-none">
                <CardContent className="p-0 flex-1 flex flex-col overflow-hidden min-h-0">
                    <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        <div className="overflow-x-auto">
                            <Table className="w-full">
                                <TableHeader className="sticky top-0 z-10 bg-gray-100">
                                    <TableRow className="bg-gray-100">
                                        <TableHead className="w-24 text-center sticky left-0 bg-gray-100 z-20 border-r-2 border-gray-300">Group No</TableHead>
                                        <TableHead className="w-32 text-center">Torch</TableHead>
                                        <TableHead className="min-w-40">Pass Name</TableHead>
                                        <TableHead className="min-w-48">New Pass Name</TableHead>
                                        <TableHead className="min-w-40">Number to Append</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {groupedPasses.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-12 text-gray-500">
                                                <div className="flex flex-col items-center gap-2">
                                                    <p>No pass data available</p>
                                                    <p className="text-xs text-gray-400">Please check console for data extraction logs</p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        groupedPasses.map(([groupKey, groupData]) => {
                                            const passes = groupData.passes || [];
                                            const torchId = groupData.torch || '';

                                            return (
                                                <TableRow key={groupKey}>
                                                    <TableCell
                                                        className="border-r-2 border-gray-300 text-center align-middle bg-gray-50 sticky left-0 z-10"
                                                    >
                                                        <span className="text-lg font-bold">{groupKey}</span>
                                                    </TableCell>
                                                    <TableCell className="text-center font-semibold align-middle py-4">
                                                        {torchId || 'N/A'}
                                                    </TableCell>
                                                    <TableCell className="text-red-600 align-top py-4">
                                                        <ul className="list-none space-y-1 pl-0">
                                                            {passes.map((pass, idx) => {
                                                                const formatted = formatPassName(pass.PassName || '', pass.PassPendName);
                                                                return (
                                                                    <li key={idx} className="flex items-start">
                                                                        <span className="text-red-600 mr-2">•</span>
                                                                        <span className="font-mono">{formatted.display}</span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </TableCell>
                                                    <TableCell className="align-top py-4">
                                                        <div>
                                                            <Select
                                                                value={renameEntries.get(groupKey)?.newPassName || ''}
                                                                onValueChange={(value) => handleNewPassNameChange(groupKey, value)}
                                                            >
                                                                <SelectTrigger className={missingFields.has(`${groupKey}-name`) ? ' border-2' : ''}>
                                                                    <SelectValue placeholder="Select Pass Name" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {passNameOptions.map((option) => (
                                                                        <SelectItem key={option.value} value={option.value}>
                                                                            {option.label}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            {missingFields.has(`${groupKey}-name`) && (
                                                                <p className="mt-1 text-xs text-red-500">Please select pass name</p>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="align-top py-4">
                                                        <div>
                                                            <Input
                                                                type="number"
                                                                value={renameEntries.get(groupKey)?.numberToAppend || ''}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleNumberChange(groupKey, e.target.value)}
                                                                min={1}
                                                                max={20}
                                                                placeholder="Number to Append (Optional)"
                                                            />
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                </CardContent>
            </Card>
        </div>
    );
};

export default RenamePass;
